import { Injectable } from '@angular/core'
import { Observable } from 'rxjs'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { environment } from '@env/environment'
import { ToastrService } from 'ngx-toastr'
import { HouseholdSearch } from 'app/models/household/household-search'
import { IHousehold } from 'app/models/household/household'
import { FormControl, FormGroup } from '@angular/forms'
import { HouseholdAssociations } from 'app/models/household/household-associations'
import { IHouseholdInteraction } from 'app/models/household/household-interaction'
import { IHouseholdDashboardViewModel } from 'app/models/household/household-dashboard.viewmodel'
import { IHouseholdLetter } from 'app/models/household/household-letter'
import { VoucherLetterEmailViewModel } from 'app/models/vouchers/voucher-letter-email-viewmodel'
import { VoucherLetter } from 'app/models/vouchers/voucher-letter'
import { HouseholdLetterUpload } from 'app/models/household/household-letter-upload'
import { IHousingUnit } from 'app/models/housingunits/housing-unit'
import { IFuzzySearchHouseholdResult } from 'app/models/common/fuzzySearchResult'

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
  }),
  withCredentials: true
};

const httpOptionsForFiles = {
  withCredentials: true
};

@Injectable({
  providedIn: 'root'
})

export class HouseholdService {
  householdExceedanceLetterType = "Exceedance";
  householdNoExceedanceLetterType = "No Exceedance";
  householdText = 'Household';
  householdCOCLetterType = 'Chain of Custody';
  search: HouseholdSearch = {
    id: '',
    contactName: '',
    address1: '',
    city: '',
    zip: '',
    searchText: '',
    county: '',
    pregnancyInHousehold: false,
    infantsUnder1: false,
    age1to4: false,
    age5to9: false,
    age10to14: false,
    age15to19: false,
    age20to29: false,
    age30to39: false,
    age40to49: false,
    age50to59: false,
    age60to69: false,
    age70to79: false,
    age80Plus: false,
    waterTreamentProvided: false,
    bottledWaterProvided: false,
    age15AndBelow: false,
    age16AndAbove: false,
    infantsInHousehold: false,
    searchBy: 'Household',
    searchResultsMaxRows: ''
  };

  searchForm = new FormGroup({
    id: new FormControl(),
    contactName: new FormControl(''),
    address1: new FormControl(''),
    city: new FormControl(''),
    zip: new FormControl(''),
    county: new FormControl(''),
    personsInHousehold: new FormControl(0),
    pregnancyInHousehold: new FormControl(false),
    waterTreamentProvided: new FormControl(false),
    bottledWaterProvided: new FormControl(false),
    infantsUnder1: new FormControl(false),
    age1to4: new FormControl(false),
    age5to9: new FormControl(false),
    age10to14: new FormControl(false),
    age15to19: new FormControl(false),
    age20to29: new FormControl(false),
    age30to39: new FormControl(false),
    age40to49: new FormControl(false),
    age50to59: new FormControl(false),
    age60to69: new FormControl(false),
    age70to79: new FormControl(false),
    age80Plus: new FormControl(false),
    age15AndBelow: new FormControl(false),
    age16AndAbove: new FormControl(false),
    infantsInHousehold: new FormControl(false),
    searchBy: new FormControl('Household')
  });

  haveSearchResults = false;
  searchResultsCount = 0;
  searchResults: IFuzzySearchHouseholdResult[] = [];
  typeOfDeclineList: string[] = ["Declined", "Unreachable"];

  constructor(private toastr: ToastrService, private http?: HttpClient) { }

  get typeOfDeclineOptions(): string[] {
    return this.typeOfDeclineList;
  }

  Search(search: HouseholdSearch): Observable<any[]> {
    return this.http.post<any[]>(environment.privateApi + 'Household/Search', search, httpOptions);
  }

  downloadCsv(search: HouseholdSearch): Observable<any> {
    this.search = search;
    return this.http.post(environment.privateApi + 'Household/DownloadCsv', search, httpOptions);
  }

  async updateAllMissingLatLong() {
    return this.http.get<any>(environment.privateApi + 'Household/UpdateAllMissingLatLong', httpOptions).toPromise();
  }

  async updateAllMissingLatLongHousingUnits() {
    return this.http.get<any>(environment.privateApi + 'Household/UpdateAllMissingLatLongHousingUnits', httpOptions).toPromise();
  }

  getHouseholdDashboard(): Observable<IHouseholdDashboardViewModel> {
    return this.http.get<IHouseholdDashboardViewModel>(environment.privateApi + 'Household/HouseholdDashboard', { withCredentials: true });
  }

  getHouseholdExceedanceLettersNotGenerated(): Observable<VoucherLetter[]> {
    return this.http.get<VoucherLetter[]>(environment.privateApi + 'Household/HouseholdExceedanceLettersNotGenerated', { withCredentials: true });
  }

  getHouseholdLettersMissingSentInfo(): Observable<VoucherLetter[]> {
    return this.http.get<VoucherLetter[]>(environment.privateApi + 'Household/HouseholdLettersMissingSentInfo', { withCredentials: true });
  }

  getHouseholdNoExceedanceLettersNotGenerated(): Observable<VoucherLetter[]> {
    return this.http.get<VoucherLetter[]>(environment.privateApi + 'Household/HouseholdNoExceedanceLettersNotGenerated', { withCredentials: true });
  }

  getHouseholdById(householdId: number): Observable<IHousehold> {
    return this.http.get<IHousehold>(environment.privateApi + 'Household/' + householdId, { withCredentials: true });
  }

  getHouseholdsByHousingUnitId(housingUnitId: number): Observable<IHousehold[]> {
    return this.http.get<IHousehold[]>(environment.privateApi + 'Household/HouseholdsByHousingUnit/' + housingUnitId, { withCredentials: true });
  }

  getHouseholdsByWellId(wellId: number): Observable<IHousehold[]> {
    return this.http.get<IHousehold[]>(environment.privateApi + 'Household/HouseholdsByWell/' + wellId, { withCredentials: true });
  }

  saveHousehold(household: IHousehold): Observable<number> {
    return this.http.post<number>(environment.privateApi + 'Household/Save', household, httpOptions);
  }

  mergeHousehold(id: number, ids: number[]): Observable<any> {
    return this.http.post(environment.privateApi + 'Household/MergeHouseholds/' + id, ids, httpOptions);
  }

  getCounties(): Observable<any> {
    return this.http.get(environment.privateApi + 'Lookups/county', { withCredentials: true });
  }

  getHouseholdAssociations(houlseholdId: number): Observable<HouseholdAssociations> {
    return this.http.get<HouseholdAssociations>(environment.privateApi + 'Household/Associations/' + houlseholdId, { withCredentials: true });
  }

  getWellHouseholds(wellId: number): Observable<IHousehold[]> {
    return this.http.get<IHousehold[]>(environment.privateApi + 'Household/WellHouseholds/' + wellId, { withCredentials: true });
  }

  disassociateHouseholdFromWell(householdId: number, wellId: number): Observable<any> {
    return this.http.post(environment.privateApi + 'Household/DisassociateWell/' + householdId + '/' + wellId, httpOptions);
  }

  disassociateHouseholdFromVoucher(householdId: number, voucherId: number): Observable<any> {
    return this.http.post(environment.privateApi + 'Household/DisassociateVoucher/' + householdId + '/' + voucherId, httpOptions);
  }

  disassociateHousingUnitFromHousehold(householdHousingUnitKey: number): Observable<any> {
    return this.http.post(environment.privateApi + 'Household/DisassociateHousingUnit/' + householdHousingUnitKey, httpOptions);
  }

  associateHouseholdToWell(householdId: number, wellId: number): Observable<any> {
    return this.http.post(environment.privateApi + 'Household/AssociateHouseholdToWell/' + householdId + '/' + wellId, httpOptions);
  }

  associateHouseholdToVoucher(householdId: number, voucherId: number): Observable<any> {
    return this.http.post(environment.privateApi + 'Household/AssociateHouseholdToVoucher/' + householdId + '/' + voucherId, httpOptions);
  }

  associateHouseholdToHousingUnit(householdAssociations: HouseholdAssociations): Observable<any> {
    return this.http.post(environment.privateApi + 'Household/AssociateHouseholdToHousingUnit', householdAssociations, httpOptions);
  }

  getHouseholdInteractions(houlseholdId: number): Observable<IHouseholdInteraction[]> {
    return this.http.get<IHouseholdInteraction[]>(environment.privateApi + 'Household/Interactions/' + houlseholdId, { withCredentials: true });
  }

  getHouseholdInteraction(id: number): Observable<IHouseholdInteraction> {
    return this.http.get<IHouseholdInteraction>(environment.privateApi + 'Household/Interaction/' + id, { withCredentials: true });
  }

  deleteHouseholdInteraction(id: number): Observable<any> {
    return this.http.get<any>(environment.privateApi + 'Household/Interactions/Delete/' + id, { withCredentials: true });
  }

  saveHouseholdInteraction(interaction: IHouseholdInteraction): Observable<number> {
    return this.http.post<number>(environment.privateApi + 'Household/Interaction/Save', interaction, httpOptions);
  }

  getHouseholdLetters(householdId: number): Observable<IHouseholdLetter[]> {
    return this.http.get<IHouseholdLetter[]>(environment.privateApi + 'Household/Letters/' + householdId, { withCredentials: true });
  }

  deleteHouseholdLetter(id: number): Observable<any> {
    return this.http.get<any>(environment.privateApi + 'Household/Letters/Delete/' + id, { withCredentials: true });
  }

  attachLetter(file: FormData): Observable<any> {
    return this.http.post<any>(environment.privateApi + 'Household/AttachLetter', file, httpOptionsForFiles);
  }

  emailLetter(test: VoucherLetterEmailViewModel): Observable<any> {
    return this.http.post(environment.privateApi + 'Household/EmailLetter/', test, httpOptions);
  }

  updateLetterInfo(letter: VoucherLetter): Observable<any> {
    return this.http.post(environment.privateApi + 'Household/UpdateLetterInfo/', letter, httpOptions);
  }

  downloadHouseholdLetter(id: number): Observable<Blob> {
    return this.http.get<Blob>(environment.privateApi + 'Household/DownloadLetter/' + id, { responseType: 'blob' as 'json', withCredentials: true });
  }

  uploadLetter(letter: HouseholdLetterUpload): Observable<any> {
    return this.http.post(environment.privateApi + 'Household/UploadLetter', letter, httpOptions);
  }



  deleteHousehold(id: number): Observable<any> {
    return this.http.get<any>(environment.privateApi + 'Household/Delete/' + id, { withCredentials: true });
  }

  getMissingLatLongReport(): Observable<IHousehold[]> {
    return this.http.get<IHousehold[]>(environment.privateApi + 'Household/Report/MissingLatLong', httpOptions);
  }

  getMultipleHousingUnitsReport(): Observable<IHousehold[]> {
    return this.http.get<IHousehold[]>(environment.privateApi + 'Household/Report/MultipleHousingUnits', httpOptions);
  }

  getMissingLatLongReportHousingUnit(): Observable<IHousingUnit[]> {
    return this.http.get<IHousingUnit[]>(environment.privateApi + 'Household/Report/MissingLatLongHousingUnits', httpOptions);
  }

  getHouseholdInteractionsApprovedReport(): Observable<IHouseholdInteraction[]> {
    return this.http.get<IHouseholdInteraction[]>(environment.privateApi + 'Household/Interactions/HouseholdInteractionsNeededReport', httpOptions);
  }

}